/**
 * API path whitelist for SmartCHG backend.
 *
 * SmartCHG keeps a strict allow-list because the SPA is older than the
 * current FastAPI surface. Unsupported paths are treated as frontend bugs
 * and blocked before a network request is made.
 *
 * Any request to a path that does not match a supported pattern is
 * short-circuited by the axios request interceptor in `api.ts` — the
 * promise resolves immediately with an empty response, no HTTP call
 * is made, no 404 noise, no error toasts.
 *
 * Update this file when the backend gains a new route. If a module is a
 * future SmartCHG feature, add its path prefix and port it in backend.
 */

// Prefixes that SmartCHG backend actively serves.
const SUPPORTED_PREFIXES: ReadonlyArray<string> = [
    // --- Auth ---
    '/auth/login',
    '/auth/refresh',
    '/auth/sso/authorize-url',
    '/auth/sso/callback',
    '/auth/sap/connect',
    '/auth/sap/connection-status',
    '/auth/sap/authorize-url',
    '/auth/sap/callback-exchange',

    // --- Profile (served by profile_compat_router) ---
    '/api/users/profile',

    // --- Health ---
    '/api/v1/health',

    // --- Conversation history (chat threads) ---
    // No trailing slash so the matcher's else branch covers both
    // `GET /api/v1/threads` (list) and `/api/v1/threads/{id}[/messages]`.
    '/api/v1/threads',

    // --- Current Smart Insights chat surface ---
    // Covers thread CRUD + orchestrator SSE stream + per-message feedback
    // (`POST /api/threads/{thread_id}/messages/{message_id}/feedback`).
    '/api/threads/',

    // --- Datasource CRUD ---
    '/api/v1/datasources',

    // --- Datasphere ---
    '/api/v1/datasphere/',

    // --- Observability ---
    '/api/v1/llm-usage/',
    '/api/v1/node-timing/',

    // --- Data source analysis (canonical SmartCHG paths + legacy alias) ---
    '/api/v1/datasource_analysis/',
    '/api/v1/data-source-analysis/',
    '/api/v1/exports',

    // --- Admin ---
    '/api/v1/admin/',

    // --- SAP intelligence module (column descriptions + NL→OData answers) ---
    '/api/v1/sap-intel/',

    // --- Allocation validation module ---
    '/api/v1/allocation/',

    // --- LLM evaluation module (trigger + poll) ---
    '/api/v1/eval/',
    
    // --- SAC embedded dashboard module URLs ---
    '/api/v1/sac-dashboards/',

    // --- Labor performance prototype reports ---
    '/api/v1/labor-performance/',
    // --- Adoption / health dashboard (super_admin only) ---
    '/api/v1/adoption/',
    // --- RedZone Downtime Analysis ---
    '/api/v1/redzone-downtime/',

    // --- OData-backed pre-canned reports ---
    '/api/v1/pre-canned-reports/',

    // --- Labor performance prototype reports ---
    '/api/v1/labor-performance/',
];

/** Absolute-path check (strips query string). Works for both `/api/...` and fully-qualified URLs. */
export function isSupportedApiPath(url: string | undefined | null): boolean {
    if (!url) return false;
    let path = url;
    try {
        if (/^https?:\/\//.test(url)) {
            path = new URL(url).pathname;
        }
    } catch {
        /* fall through */
    }
    const qIdx = path.indexOf('?');
    if (qIdx >= 0) path = path.slice(0, qIdx);

    // Only check paths under /api/ or /auth/; any other path (relative asset
    // etc.) is not our concern and is passed through.
    if (!path.startsWith('/api/') && !path.startsWith('/auth/')) return true;

    return SUPPORTED_PREFIXES.some(prefix =>
        prefix.endsWith('/') ? path.startsWith(prefix) : path === prefix || path.startsWith(prefix + '/')
    );
}

/** A record of paths that have been blocked (for browser-console diagnostics). */
const _blocked = new Set<string>();

export function recordBlocked(url: string): void {
    if (_blocked.has(url)) return;
    _blocked.add(url);
    // Log once per distinct path to avoid flooding the console.
     
    console.warn(
        `[API] blocked — endpoint not supported by SmartCHG backend: ${url}. ` +
            `If you need this, add the path to src/services/apiWhitelist.ts and implement it in the backend.`
    );
}
